import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  User, 
  Package, 
  ChevronDown, 
  ChevronUp, 
  ArrowLeft,
  Calendar,
  MapPin,
  Phone,
  Mail,
  FileImage,
  Download,
  LogOut,
  Edit2,
  Save,
  X
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import Header from './Header';
import Footer from './Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccountPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, token, isAuthenticated, logout, getOrders } = useAuth();
  
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    
    loadOrders();
    setProfileForm({
      name: user?.name || '',
      phone: user?.phone || ''
    });
  }, [isAuthenticated, user, navigate]);

  const loadOrders = async () => {
    setLoadingOrders(true);
    const userOrders = await getOrders();
    setOrders(userOrders);
    setLoadingOrders(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileForm)
      });
      
      if (response.ok) {
        // Update local storage with new user data
        const data = await response.json();
        localStorage.setItem('auth_user', JSON.stringify(data.user));
        setEditingProfile(false);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
    }
    setSavingProfile(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'processing': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'shipped': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: t('account.statusPending', 'Pendiente'),
      processing: t('account.statusProcessing', 'En Proceso'),
      shipped: t('account.statusShipped', 'Enviado'),
      completed: t('account.statusCompleted', 'Completado'),
      cancelled: t('account.statusCancelled', 'Cancelado')
    };
    return labels[status] || status;
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Back button */}
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-cyan-600 mb-6 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('account.backToHome', 'Volver al Inicio')}
          </button>

          {/* Page Title */}
          <h1 className="text-3xl font-bold text-gray-900 mb-8" data-testid="account-page-title">
            {t('account.pageTitle', 'Mi Cuenta')}
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Profile Section */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <User className="w-5 h-5 text-cyan-600" />
                      {t('account.profile', 'Perfil')}
                    </span>
                    {!editingProfile ? (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setEditingProfile(true)}
                        data-testid="edit-profile-btn"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setEditingProfile(false)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Avatar */}
                  <div className="flex justify-center mb-4">
                    <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-3xl font-bold text-white">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                  </div>

                  {editingProfile ? (
                    <div className="space-y-4">
                      <div>
                        <Label>{t('account.name', 'Nombre')}</Label>
                        <Input
                          value={profileForm.name}
                          onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
                          data-testid="profile-name-input"
                        />
                      </div>
                      <div>
                        <Label>{t('account.phone', 'Teléfono')}</Label>
                        <Input
                          value={profileForm.phone}
                          onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                          placeholder="561-123-4567"
                          data-testid="profile-phone-input"
                        />
                      </div>
                      <Button 
                        onClick={handleSaveProfile}
                        className="w-full bg-cyan-500 hover:bg-cyan-600"
                        disabled={savingProfile}
                        data-testid="save-profile-btn"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {savingProfile ? t('common.loading', 'Guardando...') : t('account.save', 'Guardar')}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 text-gray-600">
                        <User className="w-4 h-4" />
                        <span className="font-medium">{user?.name}</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600">
                        <Mail className="w-4 h-4" />
                        <span className="text-sm">{user?.email}</span>
                      </div>
                      {user?.phone && (
                        <div className="flex items-center gap-3 text-gray-600">
                          <Phone className="w-4 h-4" />
                          <span className="text-sm">{user?.phone}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Logout Button */}
                  <div className="pt-4 border-t">
                    <Button 
                      variant="outline" 
                      onClick={handleLogout}
                      className="w-full text-red-500 border-red-200 hover:bg-red-50"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('auth.logout', 'Cerrar Sesión')}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Stats Card */}
              <Card className="mt-6">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="p-3 bg-cyan-50 rounded-lg">
                      <p className="text-2xl font-bold text-cyan-600">{orders.length}</p>
                      <p className="text-xs text-gray-500">{t('account.totalOrders', 'Pedidos')}</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">
                        ${orders.reduce((sum, o) => sum + (o.total || 0), 0).toFixed(0)}
                      </p>
                      <p className="text-xs text-gray-500">{t('account.totalSpent', 'Total')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Orders Section */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-cyan-600" />
                    {t('account.myOrders', 'Mis Pedidos')}
                    <span className="text-sm font-normal text-gray-500">({orders.length})</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingOrders ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-500 mx-auto mb-4"></div>
                      <p className="text-gray-500">{t('common.loading', 'Cargando...')}</p>
                    </div>
                  ) : orders.length === 0 ? (
                    <div className="text-center py-12" data-testid="no-orders-message">
                      <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <h3 className="text-lg font-medium text-gray-700 mb-2">
                        {t('account.noOrders', 'No tienes pedidos todavía')}
                      </h3>
                      <p className="text-gray-500 mb-4">
                        {t('account.noOrdersDesc', 'Cuando realices tu primer pedido, aparecerá aquí.')}
                      </p>
                      <Button 
                        onClick={() => navigate('/')}
                        className="bg-cyan-500 hover:bg-cyan-600"
                      >
                        {t('account.startShopping', 'Comenzar a Comprar')}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {orders.map((order) => (
                        <div 
                          key={order.id} 
                          className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                          data-testid={`order-${order.id?.slice(-8)}`}
                        >
                          {/* Order Header */}
                          <div 
                            className="p-4 bg-gray-50 flex items-center justify-between cursor-pointer"
                            onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                          >
                            <div className="flex items-center gap-4">
                              <div className={`p-2 rounded-full ${getStatusColor(order.status)}`}>
                                <Package className="w-5 h-5" />
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">
                                  {t('account.orderNumber', 'Pedido')} #{order.id?.slice(-8)}
                                </p>
                                <p className="text-sm text-gray-500 flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {formatDate(order.created_at)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status)}`}>
                                  {getStatusLabel(order.status)}
                                </span>
                                <p className="font-bold text-cyan-600 mt-1">${order.total?.toFixed(2)}</p>
                              </div>
                              {expandedOrder === order.id ? (
                                <ChevronUp className="w-5 h-5 text-gray-400" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                          </div>

                          {/* Expanded Order Details */}
                          {expandedOrder === order.id && (
                            <div className="p-4 border-t bg-white">
                              {/* Shipping Address */}
                              <div className="mb-4">
                                <h4 className="font-medium text-sm text-gray-700 mb-2 flex items-center gap-2">
                                  <MapPin className="w-4 h-4 text-cyan-600" />
                                  {t('account.shippingAddress', 'Dirección de Envío')}
                                </h4>
                                <div className="bg-gray-50 p-3 rounded-lg text-sm">
                                  <p className="font-medium">{order.shipping_address?.name}</p>
                                  <p>{order.shipping_address?.street}</p>
                                  <p>
                                    {order.shipping_address?.city}, {order.shipping_address?.state} {order.shipping_address?.zip_code}
                                  </p>
                                </div>
                              </div>

                              {/* Products */}
                              <div className="mb-4">
                                <h4 className="font-medium text-sm text-gray-700 mb-2">
                                  {t('account.products', 'Productos')} ({order.items?.length || 0})
                                </h4>
                                <div className="space-y-2">
                                  {order.items?.map((item, idx) => (
                                    <div key={idx} className="bg-gray-50 p-3 rounded-lg">
                                      <div className="flex justify-between items-start">
                                        <div>
                                          <p className="font-medium text-sm">{item.product_name}</p>
                                          <p className="text-xs text-gray-500">
                                            {t('account.size', 'Tamaño')}: {item.size}" • {t('products.quantity', 'Cantidad')}: {item.quantity}
                                          </p>
                                        </div>
                                        <p className="font-medium text-cyan-600">
                                          ${(item.unit_price * item.quantity).toFixed(2)}
                                        </p>
                                      </div>
                                      
                                      {/* File attachment */}
                                      {item.uploaded_file && (
                                        <div className="mt-2 flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
                                          <FileImage className="w-4 h-4 text-green-600" />
                                          <span className="text-xs text-green-700 flex-1 truncate">
                                            {item.uploaded_file.filename}
                                          </span>
                                          <a
                                            href={`${API_URL}/api/files/${item.uploaded_file.file_id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="p-1 bg-green-100 rounded hover:bg-green-200 transition-colors"
                                          >
                                            <Download className="w-4 h-4 text-green-600" />
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Order Summary */}
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <div className="space-y-1 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">{t('cart.subtotal', 'Subtotal')}:</span>
                                    <span>${order.subtotal?.toFixed(2)}</span>
                                  </div>
                                  {order.discount_amount > 0 && (
                                    <div className="flex justify-between text-green-600">
                                      <span>{t('cart.discount', 'Descuento')} ({order.promo_code}):</span>
                                      <span>-${order.discount_amount?.toFixed(2)}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">{t('cart.shipping', 'Envío')}:</span>
                                    <span>{order.shipping_cost === 0 ? t('cart.free', 'GRATIS') : `$${order.shipping_cost?.toFixed(2)}`}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">{t('cart.tax', 'Impuesto (7%)')}:</span>
                                    <span>${order.sales_tax?.toFixed(2)}</span>
                                  </div>
                                  <div className="flex justify-between font-bold text-base pt-2 border-t mt-2">
                                    <span>{t('cart.total', 'Total')}:</span>
                                    <span className="text-cyan-600">${order.total?.toFixed(2)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AccountPage;
